/**
 * Zod schema, defaults and per-step field groups for the "dono novo" wizard.
 *
 * The schema's inferred type is reconciled with `NewMerchantFormValues`
 * (from `payload.ts`) at the `useForm<NewMerchantFormValues>(zodResolver(...))`
 * call site — if they drift, tsc fails there. Client-side validation mirrors
 * the backend constraints (`@ValidCnpj`, `@ValidCpf`, `@Email`, address
 * `\\d{8}`); the server remains the source of truth.
 */
import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';
import { isValidBrCellphone, isValidCnpj, isValidCpf, onlyDigits } from '../../lib/cpf';
import { passwordSchema } from '../../lib/password';
import { PIX_KEY_TYPE_VALUES, isValidPixKey } from '../../lib/pix';
import { strings } from './strings';
import { defaultOperatingHours, type NewMerchantFormValues, type OperatingHoursRow } from './payload';

const ZIP_DIGITS = 8;

/**
 * Person-name format (FAV-164): must start with a letter and contain only
 * letters (incl. accented), spaces, hyphen and apostrophe — no digits. Mirrors
 * the backend `@Pattern(PersonNamePatterns.PERSON_NAME)`.
 */
const PERSON_NAME_RE = /^\p{L}[\p{L} '-]*$/u;
const isPersonName = (v: string): boolean => PERSON_NAME_RE.test(v.trim());

const operatingHoursField = z
  .custom<OperatingHoursRow[]>((v) => Array.isArray(v))
  .refine((rows) => rows.some((r) => !r.closed), strings.validation.hours);

export const newMerchantSchema = z
  .object({
    // Step 1 — establishment
    tradeName: z.string().min(1, strings.validation.tradeName),
    legalName: z.string(),
    cnpj: z.string().refine(isValidCnpj, strings.validation.cnpj),
    businessType: z.enum(['RESTAURANT', 'MARKET', 'PHARMACY', 'PET_SHOP', 'CONVENIENCE', 'OTHER']),
    categoryId: z.string(),
    categoryName: z.string().min(1, strings.validation.category),
    description: z.string(),
    phone: z.string(),
    // Step 2 — responsible
    ownerFullName: z
      .string()
      .min(1, strings.validation.ownerName)
      .refine(isPersonName, strings.validation.ownerNameFormat),
    ownerCpf: z.string().refine(isValidCpf, strings.validation.cpf),
    ownerPhone: z
      .string()
      .min(1, strings.validation.phone)
      .refine(isValidBrCellphone, strings.validation.phoneFormat),
    ownerEmail: z.string().email(strings.validation.email),
    // Mirrors the backend @ValidPassword / Keycloak realm policy (12+, 4 classes).
    tempPassword: passwordSchema,
    tempPasswordRepeat: z.string().min(1, strings.validation.password),
    // Step 3 — address
    zipCode: z.string().refine((v) => onlyDigits(v).length === ZIP_DIGITS, strings.validation.zip),
    street: z.string().min(1, strings.validation.street),
    number: z.string().min(1, strings.validation.number),
    complement: z.string(),
    neighborhood: z.string().min(1, strings.validation.neighborhood),
    city: z.string().min(1, strings.validation.city),
    state: z.string().length(2, strings.validation.state),
    // Manual map pin (RF-004/RF-005) — OPTIONAL (a store may be created without a pin). Ranges mirror
    // the backend (MerchantAddressInline @DecimalMin/@DecimalMax). The pin is the source of truth.
    latitude: z.number().min(-90).max(90).nullable(),
    longitude: z.number().min(-180).max(180).nullable(),
    // Step 4 — media
    logoUrl: z.string().min(1, strings.validation.logo),
    ownerFacePhotoUrl: z.string(),
    ownerIdentityPhotoUrl: z.string(),
    // Step 5 — operations
    operatingHours: operatingHoursField,
    minimumOrderAmount: z.string(),
    estimatedPrepMinutes: z.string(),
    dispatchMode: z.enum(['OWN_COURIERS', 'GLOBAL_POOL']),
    autoAcceptOrders: z.boolean(),
    // Step 4 (Comércio) — PIX payout key (FAV-172). Optional: `''`/`''` means absent.
    // `pixKeyType` is '' when nothing is chosen; otherwise one of the PixKeyType values.
    pixKeyType: z.union([z.literal(''), z.enum(PIX_KEY_TYPE_VALUES)]),
    pixKey: z.string(),
  })
  .refine((v) => v.tempPassword === v.tempPasswordRepeat, {
    message: strings.validation.passwordMismatch,
    path: ['tempPasswordRepeat'],
  })
  .superRefine((v, ctx) => {
    // PIX é OBRIGATÓRIO no cadastro de comerciante: sem chave de repasse a loja
    // nasce sem destino de pagamento e o primeiro repasse trava. O backend segue
    // aceitando ausente (contrato inalterado) — quem exige é este formulário.
    const keyEntered = v.pixKey.trim().length > 0;
    if (!keyEntered) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: strings.validation.pixKeyRequired,
        path: ['pixKey'],
      });
      if (v.pixKeyType === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: strings.validation.pixKeyTypeRequired,
          path: ['pixKeyType'],
        });
      }
      return;
    }
    if (v.pixKeyType === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: strings.validation.pixKeyTypeRequired,
        path: ['pixKeyType'],
      });
      return;
    }
    if (!isValidPixKey(v.pixKeyType, v.pixKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: strings.validation.pixKey,
        path: ['pixKey'],
      });
    }
  });

/** Field groups validated by `form.trigger(...)` before advancing each step. */
export const STEP_FIELDS: ReadonlyArray<ReadonlyArray<keyof NewMerchantFormValues>> = [
  ['tradeName', 'cnpj', 'businessType', 'categoryName'],
  ['ownerFullName', 'ownerCpf', 'ownerPhone', 'ownerEmail', 'tempPassword', 'tempPasswordRepeat'],
  ['zipCode', 'street', 'number', 'neighborhood', 'city', 'state'],
  ['logoUrl'],
  ['operatingHours'],
  [],
];

export function newMerchantDefaults(): NewMerchantFormValues {
  return {
    tradeName: '',
    legalName: '',
    cnpj: '',
    businessType: 'RESTAURANT',
    categoryId: '',
    categoryName: '',
    description: '',
    phone: '',
    ownerFullName: '',
    ownerCpf: '',
    ownerPhone: '',
    ownerEmail: '',
    tempPassword: '',
    tempPasswordRepeat: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    latitude: null,
    longitude: null,
    logoUrl: '',
    ownerFacePhotoUrl: '',
    ownerIdentityPhotoUrl: '',
    operatingHours: defaultOperatingHours(),
    minimumOrderAmount: '',
    estimatedPrepMinutes: '',
    dispatchMode: 'OWN_COURIERS',
    autoAcceptOrders: false,
    pixKeyType: '',
    pixKey: '',
  };
}

/** Shared props for every wizard step (single typed form instance). */
export interface WizardStepProps {
  form: UseFormReturn<NewMerchantFormValues>;
}
